import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import FormModal from '../../components/FormModal';
import { 
  FileText, 
  Download, 
  CheckCircle2, 
  XCircle,
  ArrowLeft,
  MessageSquare,
  CreditCard,
  Calendar,
  User,
  Building,
  Mail,
  AlertTriangle
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';

// Interfaces (no changes)
interface QuoteItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}

interface Quote {
  _id: string; reference: string; title: string; description: string; status: any;
  items: QuoteItem[]; subtotal: number; taxRate: number; taxAmount: number; total: number;
  validUntil: string; createdAt: string; updatedAt: string;
  clientId: { _id: string; firstName: string; lastName: string; email: string; company?: string; };
  technicianId?: { _id: string; firstName: string; lastName: string; email: string; };
  notes?: string; terms?: string;
}

interface RejectFormData {
  reason: string;
}

const QuoteDetails: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<RejectFormData>();

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId) {
        toast.error('ID de devis manquant');
        navigate('/client/quotes');
        return;
      }
      setIsLoading(true);
      try {
        const response = await apiService.quotes.getById(quoteId);
        setQuote(response.data.data.quote);
      } catch (error) {
        toast.error('Erreur lors du chargement du devis.');
        navigate('/client/quotes');
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId, navigate]);

  const handleAcceptQuote = async () => {
    if (!quote) return;
    setIsAccepting(true);
    try {
      await apiService.quotes.respond(quote._id, true);
      setQuote(prev => prev ? { ...prev, status: 'accepted' } : null);
      toast.success('Devis accepté ! Vous allez être redirigé vers le paiement.');
      setTimeout(() => navigate(`/client/payment/${quoteId}`), 1500);
    } catch (error) {
      toast.error('Erreur lors de lacceptation du devis.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleRejectQuote = async (data: RejectFormData) => {
    if (!quote) return;
    setIsRejecting(true);
    try {
      await apiService.quotes.respond(quote._id, false, data.reason);
      setQuote(prev => prev ? { ...prev, status: 'rejected' } : null);
      toast.success('Devis refusé.');
      setShowRejectModal(false);
    } catch (error) {
      toast.error('Erreur lors du refus du devis.');
    } finally {
      setIsRejecting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Brouillon' },
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Envoyé' },
      accepted: { color: 'bg-green-100 text-green-800', label: 'Accepté' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Refusé' },
      expired: { color: 'bg-gray-100 text-gray-800', label: 'Expiré' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-200', label: status };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const downloadQuotePDF = async () => {
    if (!quote) return;
    try {
      const response = await apiService.quotes.downloadPdf(quote._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis-${quote.reference}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF.');
    }
  };

  if (isLoading) return <Layout><div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div></Layout>;
  if (!quote) return <Layout><div className="text-center py-12"><AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" /><h2 className="text-xl font-semibold">Devis non trouvé</h2><Button className="mt-6" onClick={() => navigate('/client/quotes')}>Retour</Button></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8 p-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/client/quotes')}><ArrowLeft className="w-4 h-4 mr-2" />Retour</Button>
          <div>
            <h1 className="text-3xl font-bold"><FileText className="w-8 h-8 inline-block mr-3 text-primary-600" />Détail du Devis</h1>
            <p className="text-gray-600 mt-1">{quote.reference} - {quote.title}</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Statut</h3>
              <div className="mt-2">{getStatusBadge(quote.status)}</div>
            </div>
            <div className="text-sm text-gray-600">
              <Calendar className="w-4 h-4 inline mr-1" />
              Valide jusqu'au {new Date(quote.validUntil).toLocaleDateString('fr-FR')}
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-gray-700">{quote.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Client</h3>
                <div className="space-y-2 text-sm">
                  {quote.clientId?.company && <div className="flex items-center"><Building className="w-4 h-4 mr-2 text-gray-400" />{quote.clientId.company}</div>}
                  <div className="flex items-center"><User className="w-4 h-4 mr-2 text-gray-400" />{quote.clientId?.firstName} {quote.clientId?.lastName}</div>
                  <div className="flex items-center"><Mail className="w-4 h-4 mr-2 text-gray-400" />{quote.clientId?.email}</div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3">Technicien</h3>
                {quote.technicianId ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center"><User className="w-4 h-4 mr-2 text-gray-400" />{quote.technicianId.firstName} {quote.technicianId.lastName}</div>
                    <div className="flex items-center"><Mail className="w-4 h-4 mr-2 text-gray-400" />{quote.technicianId.email}</div>
                  </div>
                ) : <p className="text-gray-500 text-sm">Non assigné</p>}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Détail des prestations</h3>
              <div className="bg-gray-50 rounded-lg overflow-hidden border">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">Description</th>
                      <th className="px-4 py-2 text-center text-sm font-medium">Qté</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">Prix Unitaire</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.items || []).map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">{item.description}</td>
                        <td className="px-4 py-2 text-center">{item.quantity || 0}</td>
                        <td className="px-4 py-2 text-right">{(item.unitPrice || 0).toFixed(2)}€</td>
                        <td className="px-4 py-2 text-right font-medium">{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-primary-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2"><span className="text-gray-700">Sous-total HT:</span><span className="font-medium">{(quote.subtotal || 0).toFixed(2)}€</span></div>
              <div className="flex justify-between items-center mb-2"><span className="text-gray-700">TVA ({(quote.taxRate || 0)}%):</span><span className="font-medium">{(quote.taxAmount || 0).toFixed(2)}€</span></div>
              <div className="flex justify-between items-center text-lg font-bold border-t border-primary-200 pt-2"><span>Total TTC:</span><span className="text-primary-600">{(quote.total || 0).toFixed(2)}€</span></div>
            </div>

            {(quote.notes || quote.terms) && (
              <div className="text-sm">
                {quote.notes && <div><h4 className="font-semibold mb-1">Notes</h4><p className="text-gray-600 whitespace-pre-wrap">{quote.notes}</p></div>}
                {quote.terms && <div className="mt-4"><h4 className="font-semibold mb-1">Termes et Conditions</h4><p className="text-gray-600 whitespace-pre-wrap">{quote.terms}</p></div>}
              </div>
            )}
          </div>
        </Card>

        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            <Button variant="outline" onClick={downloadQuotePDF}><Download className="w-4 h-4 mr-2" />Télécharger PDF</Button>
            <Button variant="outline" onClick={() => navigate('/client/messages')}><MessageSquare className="w-4 h-4 mr-2" />Contacter</Button>
          </div>
          {quote.status === 'sent' && (
            <div className="flex space-x-3">
              <Button variant="danger_outline" onClick={() => setShowRejectModal(true)}><XCircle className="w-4 h-4 mr-2" />Refuser</Button>
              <Button variant="success" onClick={handleAcceptQuote} isLoading={isAccepting}><CheckCircle2 className="w-4 h-4 mr-2" />Accepter</Button>
            </div>
          )}
          {quote.status === 'accepted' && (
            <Button variant="primary" onClick={() => navigate(`/client/payment/${quoteId}`)}><CreditCard className="w-4 h-4 mr-2" />Payer</Button>
          )}
        </div>

        <FormModal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Refuser le devis">
          <form onSubmit={handleSubmit(handleRejectQuote)}>
            <div className="space-y-4">
              <p>Veuillez indiquer la raison de votre refus. Cela nous aidera à améliorer nos services.</p>
              <div>
                <textarea {...register('reason', { required: 'La raison est requise' })} rows={4} placeholder="Expliquez pourquoi..." className="w-full border-gray-300 rounded-lg" />
                {errors.reason && <p className="text-sm text-red-600 mt-1">{errors.reason.message}</p>}
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <Button type="button" variant="ghost" onClick={() => setShowRejectModal(false)} disabled={isRejecting}>Annuler</Button>
              <Button type="submit" variant="danger" isLoading={isRejecting}>Confirmer le refus</Button>
            </div>
          </form>
        </FormModal>
      </div>
    </Layout>
  );
};

export default QuoteDetails;
